import { copy } from "../copy";
import { formatMoney } from "../format";
import type { LandingWin } from "../feed";
import Icon from "./Icon";

// The card beside the producer pitch shows a real paid producer from the feed
// when there is one, and otherwise how the pool splits. Never a sample
// producer or a made-up figure.
function ProducerCard({ latest }: { latest: LandingWin | undefined }) {
  if (latest) {
    return (
      <div className="mx-auto w-full max-w-md rounded-2xl border border-gold/40 bg-ground-raised p-5 md:p-6">
        <p className="text-[11px] uppercase tracking-[0.2em] text-chrome-dim">{copy.producer.lastPaid}</p>
        <div className="mt-4 flex items-center gap-4">
          <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-ground-edge bg-ground font-headline text-3xl uppercase text-gold">
            {latest.producer_username.slice(0, 1)}
          </span>
          <div className="min-w-0">
            <p className="truncate font-headline text-2xl uppercase leading-none text-chrome">@{latest.producer_username}</p>
            <p className="mt-1 truncate text-[11px] uppercase tracking-[0.16em] text-chrome-dim">{latest.contest_name}</p>
          </div>
        </div>
        <div className="mt-6 border-t border-ground-edge pt-5">
          <p className="bf-tabular font-headline text-4xl leading-none text-gold">{formatMoney(latest.payout_cents!)}</p>
          <p className="mt-1 text-[10px] uppercase tracking-[0.14em] text-chrome-dim">{copy.producer.earned}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="mx-auto w-full max-w-md rounded-2xl border border-gold/40 bg-ground-raised p-5 md:p-6">
      <p className="text-[11px] uppercase tracking-[0.2em] text-chrome-dim">{copy.producer.splitHeading}</p>
      <ul className="mt-5 space-y-4">
        {copy.producer.split.map((part, i) => (
          <li key={part.label} className="flex items-start gap-4 border-t border-ground-edge pt-4 first:border-t-0 first:pt-0">
            <span className="bf-tabular font-headline text-3xl leading-none text-gold">{String(i + 1).padStart(2, "0")}</span>
            <div>
              <p className="font-bold text-chrome">{part.label}</p>
              <p className="mt-0.5 text-xs text-chrome-dim">{part.note}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ForProducers({ winners }: { winners: LandingWin[] }) {
  const latest = winners.find((w) => (w.payout_cents ?? 0) > 0);
  return (
    <section
      id="producers"
      data-testid="producer-value"
      className="border-t border-ground-edge px-4 py-20 md:px-6 md:py-28"
    >
      <div className="mx-auto grid w-full max-w-[1240px] grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">
        <div>
          <p className="text-[11px] uppercase tracking-[0.24em] text-chrome-dim">{copy.producer.eyebrow}</p>
          <h2 className="mt-3 font-headline text-4xl uppercase leading-[0.95] tracking-[-0.01em] text-chrome md:text-6xl">
            {copy.producer.headingLead} <br />
            <span className="text-money">{copy.producer.heading}</span>
          </h2>
          <p className="mt-6 max-w-lg text-sm leading-relaxed text-chrome-dim md:text-base">{copy.producer.body}</p>
          <ul className="mt-8 space-y-3">
            {copy.producer.points.map((point) => (
              <li key={point} className="flex items-start gap-3 text-sm text-chrome">
                <Icon name="check" className="mt-0.5 text-lg text-money" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
          <a
            href="/contests"
            className="bf-focusable mt-8 inline-flex h-12 items-center rounded-md bg-money px-7 text-sm font-bold uppercase tracking-[0.12em] text-ground transition-colors hover:bg-money-deep"
          >
            {copy.producer.cta}
          </a>
        </div>
        <ProducerCard latest={latest} />
      </div>
    </section>
  );
}

export function ForFans({ hasLive }: { hasLive: boolean }) {
  return (
    <section
      id="fans"
      data-testid="fan-value"
      className="border-t border-ground-edge bg-ground-raised px-4 py-20 md:px-6 md:py-28"
    >
      <div className="mx-auto w-full max-w-[1240px]">
        <div className="max-w-2xl">
          <p className="text-[11px] uppercase tracking-[0.24em] text-chrome-dim">{copy.fan.eyebrow}</p>
          <h2 className="mt-3 font-headline text-5xl uppercase leading-[0.9] tracking-[-0.02em] text-gold md:text-7xl">
            {copy.fan.heading}
          </h2>
          <p className="mt-6 text-sm leading-relaxed text-chrome-dim md:text-base">{copy.fan.body}</p>
        </div>
        <ul className="mt-12 grid grid-cols-1 gap-6 md:mt-16 md:grid-cols-3 md:gap-8">
          {copy.fan.points.map((point) => (
            <li key={point.title} className="rounded-2xl border border-ground-edge bg-ground p-5 md:p-6">
              <span className="flex h-10 w-10 items-center justify-center rounded-md text-gold">
                <Icon name="disc" className="text-2xl" />
              </span>
              <h3 className="mt-5 font-headline text-xl uppercase tracking-[0.01em] text-chrome">{point.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-chrome-dim">{point.body}</p>
            </li>
          ))}
        </ul>
        <a
          href={hasLive ? "#live-contests" : "/contests"}
          className="bf-focusable mt-10 inline-flex h-12 items-center rounded-md border border-gold px-7 text-sm font-bold uppercase tracking-[0.12em] text-gold transition-colors hover:bg-gold/10"
        >
          {copy.fan.cta}
        </a>
      </div>
    </section>
  );
}
